import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { DragDropContext } from "react-beautiful-dnd";
import OrdersColumn from "./OrdersColumn";
import "./OrdersTab.css";

const ORDER_STATUSES = [
  { id: "todo", label: "order received" },
  { id: "inprogress", label: "processing" },
  { id: "completed", label: "completed" },
];

export default function OrdersTab({ setIsAnyModalActive }) {
  const [columns, setColumns] = useState({
    todo: [],
    inprogress: [],
    completed: [],
  });
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragLoading, setDragLoading] = useState(false);
  const prevCounts = useRef({ todo: 0, inprogress: 0, completed: 0 });
  const sounds = useRef({
    todo: new Audio("/sounds/notification.mp3"),
    inprogress: new Audio("/sounds/inprogress-order.mp3"),
    completed: new Audio("/sounds/completed-order.mp3"),
  });

  const normalizeColumns = (orders) => {
    const col = { todo: [], inprogress: [], completed: [] };
    orders.forEach((o) => {
      const status = o.status?.trim().toLowerCase();
      if (status === "to do") col.todo.push(o);
      else if (status === "in progress") col.inprogress.push(o);
      else if (status === "completed") col.completed.push(o);
    });
    return col;
  };

  const sortOrders = (orders, columnId) => {
    if (columnId === "completed") {
      return [...orders].sort(
        (a, b) => new Date(b.completed_at) - new Date(a.completed_at)
      );
    } else {
      return [...orders].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );
    }
  };

  useEffect(() => {
    let isMounted = true;
    let firstFetch = true;

    const fetchOrders = async () => {
      try {
        if (firstFetch) setLoading(true);

        const res = await axios.get(
          "https://admin-new.lasgidirestaurant.com/php/get-orders.php"
        );
        if (!isMounted) return;

        const newColumns = normalizeColumns(res.data.orders || []);

        ["todo", "inprogress", "completed"].forEach((colId) => {
          const oldCount = prevCounts.current[colId];
          const newCount = newColumns[colId].length;

          if (!firstFetch && newCount > oldCount) {
            sounds.current[colId].play().catch(() => {});
          }

          prevCounts.current[colId] = newCount;
        });

        setColumns(newColumns);
      } catch (err) {
        console.error("Failed to fetch orders:", err);
      } finally {
        if (firstFetch) setLoading(false);
        firstFetch = false;
      }
    };

    fetchOrders();
    const interval = setInterval(fetchOrders, 3000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  const toggleOrder = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const DB_STATUS_MAP = {
    todo: "to do",
    inprogress: "in progress",
    completed: "completed",
  };

  const handleDragEnd = async (result) => {
    const { destination, draggableId, source } = result;
    if (!destination) return;

    const orderId = parseInt(draggableId);
    setDragLoading(true);
    const startTime = Date.now();

    setColumns((prev) => {
      const newColumns = { ...prev };
      const sourceList = Array.from(newColumns[source.droppableId]);
      const [movedOrder] = sourceList.splice(source.index, 1);
      const destList = [...newColumns[destination.droppableId], movedOrder];
      newColumns[destination.droppableId] = sortOrders(
        destList,
        destination.droppableId
      );
      newColumns[source.droppableId] = sourceList;
      return newColumns;
    });

    sounds.current[destination.droppableId].play().catch(() => {});

    try {
      await axios.post("https://admin-new.lasgidirestaurant.com/php/update-order-status.php", {
        order_id: orderId,
        status: DB_STATUS_MAP[destination.droppableId],
      });
    } catch (err) {
      console.error("Failed to update order:", err);
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = 1500 - elapsed;
      setTimeout(() => setDragLoading(false), remaining > 0 ? remaining : 0);
    }
  };

  const totalCompletedPrice = columns.completed.reduce(
    (sum, order) => sum + (order.total_price || 0),
    0
  );

  return (
    <div className="orders-tab">
      {(loading || dragLoading) && (
        <div className="spinner-overlay">
          <div className="spinner"></div>
        </div>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="orders-columns">
          {ORDER_STATUSES.map(({ id, label }) => (
            <OrdersColumn
              key={id}
              droppableId={id}
              statusLabel={label}
              orders={columns[id]}
              expandedOrderId={expandedOrderId}
              toggleOrder={toggleOrder}
              setIsAnyModalActive={setIsAnyModalActive}
              disableDragSort={true}
              footer={
                id === "completed" && columns.completed.length > 0 ? (
                  <div className="completed-total">
                    Total Completed Orders: £{totalCompletedPrice.toFixed(2)}
                  </div>
                ) : null
              }
            />
          ))}
        </div>
      </DragDropContext>
    </div>
  );
}
